import { Table } from '@octano/global-ui';
import { useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Input, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ModalityCodesEnum } from '../../../../SectionsMaintainer/enums/modality-codes.enum';
import { CourseFormContext } from '../../providers/CourseProvider';
import {
  CourseAttendance,
  CourseFormFields,
  CourseFormMode,
} from '../../types';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

type CourseAttendanceSectionProps = {
  readOnly?: boolean;
};

/**
 * HU 15624 — Filtra keystrokes a solo dígitos. Devuelve `null` para input
 * vacío, o un entero >= 0. Garantiza que `totalHours`/`weeklyHours` nunca
 * acepten decimales, negativos ni no-numéricos (FRD §1.3.b + CP-07..09).
 */
const sanitizeIntegerHours = (raw: string): number | null => {
  const digitsOnly = raw.replace(/\D+/g, '');
  if (digitsOnly === '') {
    return null;
  }
  const parsed = parseInt(digitsOnly, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const CourseAttendanceSection = ({
  readOnly = false,
}: CourseAttendanceSectionProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const { watch, setValue } = useFormContext<CourseFormFields>();

  const { mode, formData } = useContext(CourseFormContext);
  const { activities = [], modalities = [] } = formData;

  const [attendance, modality, onlineAttendancePercentage] = watch([
    'attendance',
    'modality',
    'onlineAttendancePercentage',
  ]);

  const validActivityTypes = useMemo(
    () =>
      activities?.filter((e) =>
        attendance?.map((a) => a.activityTypeId).includes(+e.value),
      ) ?? [],
    [activities, attendance],
  );

  const selectedModality = useMemo(() => {
    if (!modality?.value) {
      return null;
    }
    const modalityData = modalities.find((m) => m.value === modality.value);
    return modalityData;
  }, [modality, modalities]);

  const showOnlineComponent = useMemo(() => {
    // @ts-ignore - deliveryMode exists in the modality data from API
    const deliveryMode = selectedModality?.deliveryMode;
    return (
      deliveryMode === ModalityCodesEnum.OL ||
      deliveryMode === ModalityCodesEnum.SP
    );
  }, [selectedModality]);

  const updateAttendanceItem = useCallback(
    (activityTypeId: number, patch: Partial<CourseAttendance>) => {
      const index = attendance.findIndex(
        (att) => att.activityTypeId === activityTypeId,
      );
      if (index < 0) {
        return;
      }
      const next = [...attendance];
      next[index] = { ...next[index], ...patch };
      setValue('attendance', next);
    },
    [attendance, setValue],
  );

  const onChangeAttendance = useCallback(
    (activityTypeId: number, value: string) => {
      if (isNaN(+value)) {
        return;
      }
      if (+value < 0 || +value > 100) {
        return;
      }
      updateAttendanceItem(activityTypeId, { minPercentage: +value });
    },
    [updateAttendanceItem],
  );

  const onChangeIntegerHours = useCallback(
    (
      activityTypeId: number,
      key: 'totalHours' | 'weeklyHours',
      raw: string,
    ) => {
      updateAttendanceItem(activityTypeId, {
        [key]: sanitizeIntegerHours(raw),
      });
    },
    [updateAttendanceItem],
  );

  const onChangeOnlineAttendance = useCallback(
    (value: string) => {
      if (Number.isNaN(+value)) {
        return;
      }
      if (+value < 0 || +value > 100) {
        return;
      }
      setValue('onlineAttendancePercentage', +value);
    },
    [setValue],
  );

  if (!activities?.length) {
    return <></>;
  }

  const isDisabled = readOnly || mode === CourseFormMode.WATCH;

  return (
    <Row>
      <Col xs={12} className="pt-4">
        <SectionTitle text={t(`assistanceRequired`)} />
      </Col>
      {mode !== CourseFormMode.WATCH && (
        <Col xs={12}>
          <p className="fs-16 fw-300">{t(`assistanceRequiredSubtitle`)}</p>
        </Col>
      )}
      <Col xs={12}>
        <Table
          columns={[
            {
              columnName: 'label',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`activityType`),
            },
            {
              columnName: 'totalHours',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`componentTotalHours`),
              cellFormat: ({ row }) => {
                const item = attendance.find(
                  (att) => att.activityTypeId === row.value,
                );
                return (
                  <div
                    className="d-flex m-auto align-items-center"
                    style={{ maxWidth: '80px' }}
                  >
                    <Input
                      name={`totalHours_${row.label}`}
                      type="text"
                      inputMode="numeric"
                      onChange={(e) =>
                        onChangeIntegerHours(
                          +row.value,
                          'totalHours',
                          e.target.value,
                        )
                      }
                      value={item?.totalHours ?? ''}
                      disabled={isDisabled}
                    />
                  </div>
                );
              },
            },
            {
              columnName: 'weeklyHours',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`componentWeeklyHours`),
              cellFormat: ({ row }) => {
                const item = attendance.find(
                  (att) => att.activityTypeId === row.value,
                );
                return (
                  <div
                    className="d-flex m-auto align-items-center"
                    style={{ maxWidth: '80px' }}
                  >
                    <Input
                      name={`weeklyHours_${row.label}`}
                      type="text"
                      inputMode="numeric"
                      onChange={(e) =>
                        onChangeIntegerHours(
                          +row.value,
                          'weeklyHours',
                          e.target.value,
                        )
                      }
                      value={item?.weeklyHours ?? ''}
                      disabled={isDisabled}
                    />
                  </div>
                );
              },
            },
            {
              columnName: 'id',
              width: '25%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`obligatoryAttendance`),
              cellFormat: ({ row }) => (
                <div
                  className="d-flex m-auto align-items-center"
                  style={{ maxWidth: '80px' }}
                >
                  <Input
                    name={`attendance_${row.label}`}
                    type="text"
                    onChange={(e) => {
                      onChangeAttendance(+row.value, e.target.value);
                    }}
                    value={
                      attendance.find((att) => att.activityTypeId === row.value)
                        ?.minPercentage ?? 0
                    }
                    disabled={isDisabled}
                  />
                  <span className="pl-2">%</span>
                </div>
              ),
            },
          ]}
          data={validActivityTypes}
          noResultsText={
            <TableEmptyContent
              title={t('assistanceRequiredSection.emptyState')}
              subtitle=""
            />
          }
        />
      </Col>
      {showOnlineComponent && (
        <Col xs={12} className="mt-3">
          <Table
            columns={[
              {
                columnName: 'label',
                width: '50%',
                thClassName: 'text-center',
                tdClassName: 'text-center',
                headerText: t(`activityType`),
              },
              {
                columnName: 'id',
                width: '50%',
                thClassName: 'text-center',
                tdClassName: 'text-center',
                headerText: t(`obligatoryAttendance`),
                cellFormat: () => (
                  <div
                    className="d-flex m-auto align-items-center"
                    style={{ maxWidth: '65px' }}
                  >
                    <Input
                      name="onlineAttendancePercentage"
                      type="text"
                      onChange={(e) => {
                        onChangeOnlineAttendance(e.target.value);
                      }}
                      value={onlineAttendancePercentage ?? 0}
                      disabled={isDisabled}
                    />
                    <span className="pl-2">%</span>
                  </div>
                ),
              },
            ]}
            data={[{ label: t('onlineComponent'), id: 'online' }]}
            noResultsText={
              <TableEmptyContent
                title={t('onlineComponentSection.emptyState')}
                subtitle=""
              />
            }
          />
        </Col>
      )}
    </Row>
  );
};

export default CourseAttendanceSection;
